/*
 * @Author: Json Chen
 * @Date: 2021-07-28 13:11:51
 * @LastEditTime: 2022-06-13 17:11:09
 * @LastEditors: Json Chen
 * @Description:
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelLocalisation/index.tsx
 */

import CommonHeader from '@/src/components/CommonHeader';
import NoPermission from '@/src/components/NoPermission';
import useBreadCrumbData from '@/src/pages/SalesCannelVersion/Hooks/useBreadCrumbData';
import AreaCodes from '@/src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelLocalisation/Components/AreaCodes';
import CommonFormItem from '@/src/pages/Settings/Localisation/Components/CommonFormItem';
import {
  getLocalisation,
  setLocalisation,
} from '@/src/request/salesChannel/setting/localisation';
import {
  isUserValid,
  salesChannelResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
  EPermissionSource,
} from '@/src/types/common/resources';
import { useRequest } from 'ahooks';
import { Form, message, Spin } from 'antd';
import React, { useEffect } from 'react'; // useState
import { useHistory, useParams } from 'react-router';
import styles from './style.module.less';
import AutoPhoneNumberValidation from './Components/AutoPhoneNumberValidation';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';

const permissions = (uuid: string) => {
  const view_localisation_setting = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelLocalisation,
        operation: EnumOperation.view_localisation_setting,
        uuid,
      }),
    ],
  });
  const edit_localisation_setting = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelLocalisation,
        operation: EnumOperation.edit_localisation_setting,
        uuid,
      }),
    ],
  });

  return {
    view_localisation_setting,
    edit_localisation_setting,
  };
};

const Localisation = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const [form] = Form.useForm();
  const history = useHistory();

  const { uuid } = useParams<{ uuid: string }>();
  const { view_localisation_setting, edit_localisation_setting } =
    permissions(uuid);
  const [onSaveDisable, setOnSaveDisable] = React.useState<boolean>(true);
  const [localisationSettingUuid, setLocalisationSettingUuid] =
    React.useState<string>('');
  const [hasError, setHasError] = React.useState<boolean>(false);
  const [timezoneOptions, setTimezoneOptions] = React.useState([]);

  const { loading: breadCrumbLoading, breadcrumbData } = useBreadCrumbData({
    lastSecond: getI18n('general.sider.settings'),
    lastOne: getI18n('salesChannelLocalisation.index.localisation'),
  });

  const { loading: setLocalisationLoading, run: _setLocalisation } = useRequest(
    (params) => setLocalisation(params),
    {
      manual: true,
      onSuccess: (result) => {
        message.success(getI18n('general.savesuccess'));
        onGoBackClick();
      },
    },
  );

  const { loading: getLocalisationLoading, run: _getLocalisation } = useRequest(
    (uuid) => getLocalisation(uuid),
    {
      manual: true,
      ready: !!view_localisation_setting,
      onSuccess(result) {
        if (result && result.localisationSetting && result.timezones) {
          const { timezones, localisationSetting } = result;
          const {
            uuid: suuid,
            timezone,
            dateFormat,
            timeFormat,
            decimalSeparator,
            thousandsSeparator,
            areaCodes,
            googleAutoCompleteEnabled,
            googlePlaceKey,
            autoPhoneValidation,
          } = localisationSetting;

          setTimezoneOptions(timezones);

          if (suuid) {
            setLocalisationSettingUuid(suuid);
          }

          form.setFieldsValue({
            timezone,
            dateFormat,
            timeFormat,
            decimalSeparator,
            thousandsSeparator,
            areaCodes: areaCodes || [],
            googleAutoCompleteEnabled,
            googlePlaceKey,
            autoPhoneValidation: autoPhoneValidation || {
              enabled: false,
              gatewayUuid: null,
            },
          });
        }
      },
    },
  );

  const onValuesChange = () => {
    if (onSaveDisable) {
      setOnSaveDisable(!onSaveDisable);
    }
  };

  const onFormSubmit = () => {
    form.validateFields().then((res) => {
      console.log('res', res);
      const params = { ...res, salesChannelUuid: uuid };
      if (localisationSettingUuid) {
        params.uuid = localisationSettingUuid;
      }
      _setLocalisation(params);
    });
  };

  const onGoBackClick = () => {
    history.push(`/salesChannel/settings/${uuid}`);
  };

  useEffect(() => {
    if (view_localisation_setting) {
      _getLocalisation(uuid);
    }
  }, []);

  return view_localisation_setting ? (
    <div className={`${styles.salesChannelLocalisation} ${styles.container}`}>
      <Spin
        spinning={
          getLocalisationLoading || setLocalisationLoading || breadCrumbLoading
        }>
        <Form
          name={'localisation'}
          onValuesChange={onValuesChange}
          form={form}
          colon={false}
          layout={'vertical'}
          labelCol={{
            span: 8,
          }}
          wrapperCol={{
            span: 24,
          }}
          className={styles.editForm}>
          <CommonHeader
            breadcrumbData={breadcrumbData}
            onSaveDisable={onSaveDisable || !edit_localisation_setting}
            onSubmit={onFormSubmit}
            onDiscard={onGoBackClick}
            onGoBackClick={onGoBackClick}
          />
          <div className={styles.commonContainer}>
            {/* Reporting Date, Time and Numbers Format */}
            <div className={styles.reportingDate}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n(
                    'salesChannelLocalisation.index.dateTimeandNumbersFormat',
                  )}
                </div>
              </div>
              <div className={styles.content}>
                <CommonFormItem
                  timezoneOptions={timezoneOptions}
                  disableEdit={!edit_localisation_setting}
                />
              </div>
            </div>
            {/* Address Format Localisation */}
            <div className={styles.addressFormat}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n('salesChannelLocalisation.index.phoneNumber')}
                </div>
              </div>
              {/* <div className={styles.content}> */}

              <Form.Item
                name={'areaCodes'}
                rules={[
                  {
                    validator(_, value) {
                      if (!value || !value.length) {
                        setHasError(true);
                        return Promise.reject();
                      }
                      setHasError(false);
                      return Promise.resolve();
                    },
                  },
                ]}>
                <AreaCodes
                  hasError={hasError}
                  disableEdit={!edit_localisation_setting}
                />
              </Form.Item>

              <Form.Item
                name='autoPhoneValidation' //name='phoneAutoValidation'
              >
                <AutoPhoneNumberValidation />
              </Form.Item>
              {/* </div> */}
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  ) : (
    <NoPermission />
  );
};
export default Localisation;
